// src/pages/teacher/TeacherExamVIew.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Card, Row, Col, Badge, Button, Spinner, Alert, Form, Modal, Dropdown, ButtonGroup
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection, query, where, onSnapshot, doc, updateDoc, writeBatch,
  orderBy, serverTimestamp, deleteDoc, getDoc
} from "firebase/firestore";
import { db } from "../../../services/firebase";
import useSchool from "../../../hooks/useSchool";
import { useAuth } from "../../../context/AuthContext";

/* ===== Helpers ===== */
const TYPES_CAN_SHUFFLE_OPTIONS = new Set(["mcq", "multi_correct", "mcq_descriptive"]);
const by = (key, dir = "asc") => (a, b) => {
  const av = a?.[key]; const bv = b?.[key];
  const r = av === bv ? 0 : (av > bv ? 1 : -1);
  return dir === "desc" ? -r : r;
};
const rnd = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};
const getType = (q) => String(q?.questionType || "mcq").toLowerCase();

// Labels for MATCH
const leftLabel = (idx) => {
  let n = idx + 1, s = "";
  while (n > 0) { n--; s = String.fromCharCode(97 + (n % 26)) + s; n = Math.floor(n / 26); }
  return s; // a, b, c...
};
const toRoman = (num) => {
  const map = [[1000,"m"],[900,"cm"],[500,"d"],[400,"cd"],[100,"c"],[90,"xc"],[50,"l"],[40,"xl"],[10,"x"],[9,"ix"],[5,"v"],[4,"iv"],[1,"i"]];
  let res = "", n = Math.max(1, Math.floor(num));
  for (const [v,s] of map) while (n >= v) { res += s; n -= v; }
  return res;
};
const rightLabel = (idx) => toRoman(idx + 1);
const formatMatchOptionText = (arr = []) =>
  (!arr.length || arr.some(v => v == null))
    ? "—"
    : arr.map((rIdx, lIdx) => `(${leftLabel(lIdx)})-(${rightLabel(rIdx)})`).join(", ");

/* ===== Main ===== */
export default function TeacherExamVIew() {
  const { id: templateId } = useParams();
  const nav = useNavigate();
  const { schoolId, loading: schoolLoading } = useSchool();
  const { user } = useAuth();

  const [tpl, setTpl] = useState(null);
  const [tplLoading, setTplLoading] = useState(true);

  const [qs, setQs] = useState([]);         // questions raw
  const [qsLoading, setQsLoading] = useState(true);

  // cache of source bank docs (questionbank/{bankId}) to enrich preview
  const [bankCache, setBankCache] = useState({}); // { [bankId]: docData }

  const [savingOrder, setSavingOrder] = useState(false);
  const [savingOptions, setSavingOptions] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [busy, setBusy] = useState({});     // {id: boolean}

  // let the UI show when we're running without the composite index
  const [fallbackNoIndex, setFallbackNoIndex] = useState(false);

  /* ---- subscribe template ---- */
  useEffect(() => {
    if (!schoolId || !templateId) return;
    setTplLoading(true);
    const u = onSnapshot(
      doc(db, "schools", schoolId, "examTemplates", templateId),
      (snap) => { setTpl(snap.exists() ? { id: snap.id, ...(snap.data()||{}) } : null); setTplLoading(false); },
      () => setTplLoading(false)
    );
    return () => u();
  }, [schoolId, templateId]);

  /* ---- subscribe questions for template (ordered, with safe fallback) ---- */
  useEffect(() => {
    if (!schoolId || !templateId) return;
    setQsLoading(true);

    const ref = collection(db, "schools", schoolId, "questions");
    const orderedQ = query(ref, where("templateId", "==", templateId), orderBy("createdAt", "asc"));

    // Attach the main listener; if it errors with failed-precondition, switch to fallback
    let unsubMain = onSnapshot(
      orderedQ,
      (snap) => {
        const rows = snap.docs.map(d => ({ id: d.id, ...(d.data()||{}) }));
        setQs(rows);
        setQsLoading(false);
        setFallbackNoIndex(false);
      },
      (err) => {
        if (err?.code === "failed-precondition") {
          // Index missing — listen without orderBy so the page keeps working
          try { unsubMain && unsubMain(); } catch {}
          const noOrderQ = query(ref, where("templateId", "==", templateId));
          unsubMain = onSnapshot(
            noOrderQ,
            (snap2) => {
              const rows2 = snap2.docs.map(d => ({ id: d.id, ...(d.data()||{}) }));
              setQs(rows2);
              setQsLoading(false);
              setFallbackNoIndex(true);
            },
            (err2) => {
              console.error("Questions fallback listener error:", err2);
              setQs([]);
              setQsLoading(false);
            }
          );
        } else {
          console.error("Questions listener error:", err);
          setQs([]);
          setQsLoading(false);
        }
      }
    );

    return () => { try { unsubMain && unsubMain(); } catch {} };
  }, [schoolId, templateId]);

  /* ---------- Enrich from questionbank when fields are missing ---------- */
  const NEED_KEYS = [
    "options","descPartHtml","explanationHtml",
    "trueFalseAnswer","assertionHtml","reasonHtml","arAnswer",
    "statements",
    "matchPairs","matchOptions","correctOptionIndex","matchPairCount",
    "passageHtml","caseSubQs","correctKey"
  ];
  const needsEnrich = (q) => {
    const t = getType(q);
    if (TYPES_CAN_SHUFFLE_OPTIONS.has(t)) return !(Array.isArray(q.options) && q.options.length);
    if (t === "true_false") return !q.trueFalseAnswer;
    if (t === "assertion_reason") return !q.arAnswer || !q.assertionHtml || !q.reasonHtml;
    if (t === "statement_based") return !(Array.isArray(q.statements) && q.statements.length) && !(Array.isArray(q.options) && q.options.length);
    if (t === "match") return !(Array.isArray(q.matchPairs) && q.matchPairs.length) && !(Array.isArray(q.matchOptions) && q.matchOptions.length);
    if (t === "case_based") return !(Array.isArray(q.caseSubQs) && q.caseSubQs.length);
    return !q.explanationHtml; // descriptive
  };

  useEffect(() => {
    let cancel = false;
    async function enrich() {
      if (!schoolId) return;
      const ids = [...new Set(
        qs.filter(q => q?.source?.bankId && needsEnrich(q)).map(q => q.source.bankId)
      )].filter(id => !bankCache[id]);
      if (ids.length === 0) return;
      const results = {};
      await Promise.all(ids.map(async (id) => {
        try {
          const snap = await getDoc(doc(db, "questionbank", id));
          if (!cancel && snap.exists()) results[id] = { id, ...(snap.data()||{}) };
        } catch { /* ignore */ }
      }));
      if (!cancel && Object.keys(results).length) {
        setBankCache(prev => ({ ...prev, ...results }));
      }
    }
    enrich();
    return () => { cancel = true; };
  }, [schoolId, qs, bankCache]);

  /** Merge any missing fields from bank cache (preview-only) */
  const effective = (q) => {
    const bank = q?.source?.bankId ? bankCache[q.source.bankId] : null;
    if (!bank) return q;
    let changed = false;
    const out = { ...q };
    NEED_KEYS.forEach((k) => {
      if (out[k] == null && bank[k] != null) { out[k] = bank[k]; changed = true; }
    });
    return changed ? out : q;
  };

  /* ===== Subject/Section ordering from template ===== */
  const tplSubjectOrder = useMemo(() => {
    const sw = Array.isArray(tpl?.subjectWise) ? tpl.subjectWise : [];
    return sw.map((s, idx) => ({
      idx,
      subjectId: s.subjectId || "",
      subjectName: s.subjectName || s.subjectId || "(Untitled)",
      sections: (Array.isArray(s.sections) ? s.sections : []).map((sec, i) => ({
        idx: i,
        key: (sec.key || sec.label || "A"),
        label: sec.label || sec.key || "A",
      }))
    }));
  }, [tpl?.subjectWise]);

  /* ===== Grouping & ordering ===== */
  const grouped = useMemo(() => {
    const bucket = new Map(); // subject -> Map(sectionKey -> [questions])
    qs.forEach(q0 => {
      const q = { ...q0, questionType: getType(q0) };
      const subj = q.subject || "(Subject)";
      const secKey = q.sectionKey || "";
      if (!bucket.has(subj)) bucket.set(subj, new Map());
      const m = bucket.get(subj);
      if (!m.has(secKey)) m.set(secKey, []);
      m.get(secKey).push(q);
    });
    for (const [, secMap] of bucket) {
      for (const [key, arr] of secMap) {
        arr.sort((a, b) => {
          const ao = Number.isFinite(+a.orderIndex) ? +a.orderIndex : Number.POSITIVE_INFINITY;
          const bo = Number.isFinite(+b.orderIndex) ? +b.orderIndex : Number.POSITIVE_INFINITY;
          if (ao !== bo) return ao - bo;
          const at = a.createdAt?.toDate?.() ? a.createdAt.toDate().getTime() : 0;
          const bt = b.createdAt?.toDate?.() ? b.createdAt.toDate().getTime() : 0;
          return at - bt;
        });
      }
    }
    return bucket;
  }, [qs]);

  const orderedList = useMemo(() => {
    const out = [];
    const doneSubject = new Set();

    // 1) follow template subject/section order
    tplSubjectOrder.forEach(srow => {
      const subj = srow.subjectName;
      doneSubject.add(subj);
      const secMap = grouped.get(subj);
      if (!secMap) return;

      const doneSec = new Set();
      srow.sections.forEach(sec => {
        const arr = secMap.get(sec.key) || secMap.get(sec.label) || [];
        doneSec.add(sec.key); doneSec.add(sec.label);
        arr.forEach(q => out.push({ ...q, _sLabel: subj, _secKey: sec.key, _secLabel: sec.label }));
      });
      for (const [k, arr] of secMap.entries()) {
        if (doneSec.has(k)) continue;
        arr.forEach(q => out.push({ ...q, _sLabel: subj, _secKey: k, _secLabel: k }));
      }
    });

    // 2) subjects not in the template list
    for (const [subj, secMap] of grouped.entries()) {
      if (doneSubject.has(subj)) continue;
      for (const [k, arr] of secMap.entries()) {
        arr.forEach(q => out.push({ ...q, _sLabel: subj, _secKey: k, _secLabel: k }));
      }
    }

    return out.map((q, i) => ({ ...q, _num: i + 1 }));
  }, [grouped, tplSubjectOrder]);

  const ranges = useMemo(() => {
    const res = [];
    let cursor = 1;
    const mapBySubject = new Map();
    orderedList.forEach(row => {
      const s = row._sLabel || "(Subject)";
      const k = row._secKey || "";
      if (!mapBySubject.has(s)) mapBySubject.set(s, new Map());
      const mm = mapBySubject.get(s);
      if (!mm.has(k)) mm.set(k, []);
      mm.get(k).push(row);
    });
    for (const [s, mm] of mapBySubject.entries()) {
      const sections = [];
      for (const [k, arr] of mm.entries()) {
        const start = cursor;
        const end = cursor + arr.length - 1;
        sections.push({ key: k, label: arr[0]?._secLabel || k || "—", start, end, count: arr.length });
        cursor = end + 1;
      }
      res.push({ subject: s, sections });
    }
    return res;
  }, [orderedList]);

  /* ===== View list & actions ===== */
  const [viewList, setViewList] = useState([]);
  useEffect(() => { setViewList(orderedList); }, [orderedList]);

  const subjectLabels = useMemo(() => Array.from(new Set(viewList.map(x => x._sLabel))), [viewList]);
  const sectionKeys = (subj) => Array.from(new Set(viewList.filter(x => x._sLabel === subj).map(x => x._secKey)));

  const shuffleQuestions = (scope = "all", scopeKey = null) => {
    const next = [...viewList];
    if (scope === "all") {
      const groupMap = new Map();
      next.forEach(q => {
        const k = `${q._sLabel}__${q._secKey}`;
        if (!groupMap.has(k)) groupMap.set(k, []);
        groupMap.get(k).push(q);
      });
      const out = [];
      for (const [, arr] of groupMap) rnd(arr).forEach(x => out.push(x));
      out.forEach((q, i) => q._num = i + 1);
      setViewList(out);
      return;
    }
    if (scope === "subject") {
      const subj = scopeKey;
      const out = [];
      const parts = next.reduce((acc, q) => {
        const key = q._sLabel === subj ? "X" : `${q._sLabel}__${q._secKey}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(q);
        return acc;
      }, {});
      Object.entries(parts).forEach(([k, arr]) => {
        if (k === "X") rnd(arr).forEach(x => out.push(x));
        else arr.forEach(x => out.push(x));
      });
      out.forEach((q, i) => q._num = i + 1);
      setViewList(out);
      return;
    }
    if (scope === "section") {
      const [subj, sec] = scopeKey;
      const out = [];
      const parts = next.reduce((acc, q) => {
        const match = (q._sLabel === subj && q._secKey === sec);
        const key = match ? "X" : `${q._sLabel}__${q._secKey}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(q);
        return acc;
      }, {});
      Object.entries(parts).forEach(([k, arr]) => {
        if (k === "X") rnd(arr).forEach(x => out.push(x));
        else arr.forEach(x => out.push(x));
      });
      out.forEach((q, i) => q._num = i + 1);
      setViewList(out);
    }
  };

  const saveOrder = async () => {
    if (!schoolId) return;
    try {
      setSavingOrder(true);
      const batch = writeBatch(db);
      viewList.forEach(row => {
        const ref = doc(db, "schools", schoolId, "questions", row.id);
        batch.update(ref, { orderIndex: row._num, updatedAt: serverTimestamp() });
      });
      await batch.commit();
      alert("Order saved.");
    } catch (e) {
      alert(e?.message || "Failed to save order.");
    } finally {
      setSavingOrder(false);
    }
  };
  const resetOrder = () => setViewList(orderedList);
 const shuffleOptionsForVisible = () => {
  // Use bank-enriched data so MCQs that only have options in the bank also shuffle
    const next = viewList.map((raw) => {
      const qEff = effective(raw);                      // <- merged with bankCache if needed
      if (!TYPES_CAN_SHUFFLE_OPTIONS.has(getType(qEff))) return raw;
      // Prefer already-drafted options if you’re shuffling again
      const base = Array.isArray(qEff._optsDraft) && qEff._optsDraft.length >= 2
        ? qEff._optsDraft
        : (Array.isArray(qEff.options) ? qEff.options : []);
      if (base.length < 2) return raw;
      return { ...raw, _optsDraft: rnd(base) };
    });
        setViewList(next);
  };
  const saveOptionShuffle = async () => {
    if (!schoolId) return;
    const changed = viewList.filter(q => q._optsDraft && TYPES_CAN_SHUFFLE_OPTIONS.has(getType(q)));
    if (changed.length === 0) { alert("No MCQ options are shuffled."); return; }
    try {
      setSavingOptions(true);
      const chunk = (arr, n) => arr.reduce((a,_,i) => (i % n ? a : [...a, arr.slice(i, i+n)]), []);
      for (const part of chunk(changed, 400)) {
        const batch = writeBatch(db);
        part.forEach(row => batch.update(
          doc(db, "schools", schoolId, "questions", row.id),
          { options: row._optsDraft, updatedAt: serverTimestamp() }
        ));
        await batch.commit();
      }
      alert("Shuffled options saved for MCQ-style questions.");
    } catch (e) {
      alert(e?.message || "Failed to save option shuffle.");
    } finally {
      setSavingOptions(false);
    }
  };

  const updateMeta = async (row, patch) => {
    if (!schoolId || !row?.id) return;
    try {
      setBusy(b => ({ ...b, [row.id]: true }));
      await updateDoc(
        doc(db, "schools", schoolId, "questions", row.id),
        { ...patch, updatedAt: serverTimestamp() }
      );
    } catch (e) {
      alert(e?.message || "Update failed.");
    } finally {
      setBusy(b => ({ ...b, [row.id]: false }));
    }
  };
  const removeQuestion = async (row) => {
    if (!schoolId || !row?.id) return;
    const ok = window.confirm("Delete this question from template?");
    if (!ok) return;
    try {
      setBusy(b => ({ ...b, [row.id]: true }));
      await deleteDoc(doc(db, "schools", schoolId, "questions", row.id));
    } catch (e) {
      alert(e?.message || "Delete failed.");
    } finally {
      setBusy(b => ({ ...b, [row.id]: false }));
    }
  };

  /* ===== UI ===== */
  if (schoolLoading || tplLoading || qsLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <Spinner />
      </div>
    );
  }
  if (!tpl) return <Alert variant="warning">Template not found.</Alert>;

  /* ---------- Render helpers for PREVIEW ---------- */
  const letter = (i) => String.fromCharCode(65 + i);

  const renderOptionsBlock = (rawQ) => {
    const q = effective(rawQ);
    const type = getType(q);

    if (TYPES_CAN_SHUFFLE_OPTIONS.has(type)) {
      const opts = (q._optsDraft || q.options || []);
      if (!opts.length) return null;
      return (
        <ol className="mt-2" type="A">
          {opts.map((o, idx) => (
            <li key={o?.key || idx}>
              <div dangerouslySetInnerHTML={{ __html: o?.value || "<em class='text-muted'>—</em>" }} />
            </li>
          ))}
        </ol>
      );
    }

    if (type === "true_false") {
      return (
        <ul className="mt-2 mb-0">
          <li>True</li>
          <li>False</li>
        </ul>
      );
    }

    if (type === "assertion_reason") {
      return (
        <div className="mt-2">
          <div className="mb-1"><strong>Assertion</strong></div>
          <div className="mb-2 qb-html" dangerouslySetInnerHTML={{ __html: q.assertionHtml || "" }} />
          <div className="mb-1"><strong>Reason</strong></div>
          <div className="qb-html" dangerouslySetInnerHTML={{ __html: q.reasonHtml || "" }} />
          <ol className="mt-2 mb-0" type="A">
            <li>Both A and R are true, and R is the correct explanation of A</li>
            <li>Both A and R are true, but R is not the correct explanation of A</li>
            <li>A is true but R is false</li>
            <li>A is false but R is true</li>
          </ol>
        </div>
      );
    }

    if (type === "statement_based") {
      const stmts = q.statements || [];
      return (
        <div className="mt-2">
          {stmts.map((s, i) => (
            <div key={s?.id || i} className="mb-1">
              <strong>{letter(i)}.</strong>{" "}
              <span className="qb-html" dangerouslySetInnerHTML={{ __html: s?.html || "" }} />
            </div>
          ))}
          {Array.isArray(q.options) && q.options.length ? (
            <ol className="mt-2" type="A">
              {q.options.map((o, i) => (
                <li key={o?.key || i}><div className="qb-html" dangerouslySetInnerHTML={{ __html: o?.value || "" }} /></li>
              ))}
            </ol>
          ) : null}
        </div>
      );
    }

    if (type === "match") {
      if (Array.isArray(q.matchPairs) && q.matchPairs.length) {
        return (
          <table className="table table-bordered table-sm mt-2">
            <thead><tr><th>List-I</th><th>List-II</th></tr></thead>
            <tbody>
            {q.matchPairs.filter(r => r.leftHtml || r.rightHtml).map((r, i) => (
              <tr key={r.id || i}>
                <td className="qb-html" dangerouslySetInnerHTML={{ __html: r.leftHtml || "" }} />
                <td className="qb-html" dangerouslySetInnerHTML={{ __html: r.rightHtml || "" }} />
              </tr>
            ))}
            </tbody>
          </table>
        );
      }
      const opts = Array.isArray(q.matchOptions) ? q.matchOptions : [];
      return (
        <div className="mt-2">
          {opts.map((arr, i) => (
            <div key={i} className="mb-1">
              <Badge bg={q.correctOptionIndex === i ? "success" : "secondary"} className="me-2">
                Option {i+1}
              </Badge>
              <span className="text-muted">{formatMatchOptionText(arr)}</span>
            </div>
          ))}
        </div>
      );
    }

    if (type === "case_based") {
      return (
        <div className="mt-2">
          {q.passageHtml && (
            <>
              <div className="text-muted small mb-1">Passage / Case</div>
              <div className="qb-html mb-2" dangerouslySetInnerHTML={{ __html: q.passageHtml }} />
            </>
          )}
          {(q.caseSubQs || []).map((sq, idx) => (
            <div key={sq?.id || idx} className="mb-3">
              <div className="fw-semibold">Sub Q{idx+1}</div>
              <div className="qb-html" dangerouslySetInnerHTML={{ __html: sq?.questionHtml || "" }} />
              <ol className="mt-2 mb-1" type="A">
                {(sq?.options || []).map((o, i) => (
                  <li key={o?.key || i}>
                    <div className="qb-html" dangerouslySetInnerHTML={{ __html: o?.value || "" }} />
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      );
    }

    if (type === "descriptive") return null;

    return null;
  };

  const renderStemBlock = (rawQ) => {
    const q = effective(rawQ);
    const type = getType(q);
    if (type === "case_based") return null; // passage shown in options block
    return <div className="qb-html" dangerouslySetInnerHTML={{ __html: q.questionHtml || "" }} />;
  };

  const computeAnswerString = (rawQ) => {
    const q = effective(rawQ);
    const type = getType(q);
    const letter = (i) => String.fromCharCode(65 + i);

    if (TYPES_CAN_SHUFFLE_OPTIONS.has(type)) {
      const fromOpts = (q._optsDraft || q.options || [])
        .map((o, i) => ({ ...o, idx: i }))
        .filter(o => o?.isCorrect)
        .map(o => letter(o.idx));
      if (fromOpts.length) return fromOpts.join(", ");
      if (q.correctKey) return q.correctKey;
      if (Number.isFinite(+q.correctOptionIndex)) return letter(+q.correctOptionIndex);
      return "—";
    }
    if (type === "true_false") return q.trueFalseAnswer || "True";
    if (type === "assertion_reason") return q.arAnswer || "A";
    if (type === "statement_based") {
      const stmts = q.statements || [];
      if (stmts.length) return stmts.map((s, i) => `${letter(i)}:${s?.isTrue ? "T" : "F"}`).join(", ");
      const idxs = (q.options || []).reduce((acc, o, i) => (o?.isCorrect ? acc.concat(i) : acc), []);
      if (idxs.length) return idxs.map(letter).join(", ");
      return "—";
    }
    if (type === "match") {
      if (Array.isArray(q.matchPairs) && q.matchPairs.length) {
        return q.matchPairs
          .filter(r => r.leftHtml || r.rightHtml)
          .map((_, i) => `(${leftLabel(i)})-(${rightLabel(i)})`)
          .join(", ");
      }
      if (Array.isArray(q.matchOptions) && q.matchOptions.length) {
        const idx = Number.isFinite(+q.correctOptionIndex) ? +q.correctOptionIndex : -1;
        if (idx >= 0 && q.matchOptions[idx]) return formatMatchOptionText(q.matchOptions[idx] || []);
        return formatMatchOptionText(q.matchOptions[0] || []);
      }
      return "—";
    }
    if (type === "case_based") {
      const subs = q.caseSubQs || [];
      const lines = subs.map((sq, i) => {
        if (sq?.correctKey) return `Q${q._num}.${i+1}: ${sq.correctKey}`;
        const idx = (sq?.options || []).findIndex(o => o?.isCorrect);
        return `Q${q._num}.${i+1}: ${idx >= 0 ? letter(idx) : "—"}`;
      });
      return lines.join("  •  ");
    }
    return "—";
  };

  return (
    <div className="container-fluid p-0">
      {/* Header */}
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div>
          <div className="tdb-eyebrow">Teacher • View Template</div>
          <h5 className="m-0 fw-bold">{tpl.name || "Template"}</h5>
          <div className="text-muted small">
            {orderedList.length} questions · {String(tpl.status || "draft").toUpperCase()}
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => nav(-1)}>
            <i className="bi bi-arrow-left-short me-1" /> Back
          </Button>
          <Button variant="outline-primary" onClick={() => nav(`/teacher/exams/templates/${tpl.id}/questions`)} title="Open Question Bank">
            <i className="bi bi-plus-square me-1" /> Import More
          </Button>
          <Button variant="dark" onClick={() => setPreviewOpen(true)}>
            <i className="bi bi-eye me-1" /> Preview Paper
          </Button>
        </div>
      </div>

      {fallbackNoIndex && (
        <Alert variant="warning" className="py-2">
          Running without composite index (<code>templateId</code> + <code>createdAt</code>).  
          Create it in Firestore → Indexes to enable server-side ordering. (This page is using a safe fallback meanwhile.)
        </Alert>
      )}

      <Row className="g-3">
        {/* Left: Subject/Section Ranges */}
        <Col lg={4}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent fw-semibold">Overview (Subject → Section ranges)</Card.Header>
            <Card.Body>
              {ranges.length === 0 ? (
                <div className="text-muted">No questions in this template.</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {ranges.map(block => (
                    <div key={block.subject}>
                      <div className="fw-semibold mb-1">{block.subject}</div>
                      <div className="d-flex flex-wrap gap-1">
                        {block.sections.map(sec => (
                          <Badge key={`${block.subject}-${sec.key}`} bg="light" text="dark" className="border">
                            {sec.label}: {sec.count > 0 ? `${sec.start}–${sec.end}` : "—"}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Shuffle / Save controls */}
          <Card className="border-0 shadow-sm mt-3">
            <Card.Header className="bg-transparent fw-semibold">Order & Shuffle</Card.Header>
            <Card.Body>
              <div className="d-flex flex-column gap-2">
                <Button variant="outline-primary" onClick={() => shuffleQuestions("all")}>
                  Shuffle all (keeps ranges by section)
                </Button>

                <Dropdown as={ButtonGroup}>
                  <Button
                    variant="outline-primary"
                    onClick={() => subjectLabels[0] && shuffleQuestions("subject", subjectLabels[0])}
                    disabled={subjectLabels.length === 0}
                  >
                    Shuffle subject…
                  </Button>
                  <Dropdown.Toggle split variant="outline-primary" />
                  <Dropdown.Menu>
                    {subjectLabels.length === 0 && <Dropdown.Item disabled>No subjects</Dropdown.Item>}
                    {subjectLabels.map(s => (
                      <Dropdown.Item key={s} onClick={() => shuffleQuestions("subject", s)}>
                        {s}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                <Dropdown as={ButtonGroup}>
                  <Button
                    variant="outline-primary"
                    onClick={() => {
                      const s = subjectLabels[0];
                      const sec = s ? (sectionKeys(s)[0] || "") : "";
                      if (s) shuffleQuestions("section", [s, sec]);
                    }}
                    disabled={subjectLabels.length === 0}
                  >
                    Shuffle section…
                  </Button>
                  <Dropdown.Toggle split variant="outline-primary" />
                  <Dropdown.Menu style={{ maxHeight: 300, overflowY: "auto" }}>
                    {subjectLabels.map(s => (
                      <div key={s}>
                        <Dropdown.Header className="text-muted">{s}</Dropdown.Header>
                        {sectionKeys(s).map(k => (
                          <Dropdown.Item key={`${s}::${k}`} onClick={() => shuffleQuestions("section", [s, k])}>
                            {k || "(No key)"}
                          </Dropdown.Item>
                        ))}
                        <Dropdown.Divider />
                      </div>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                <hr />
                <Button variant="primary" onClick={saveOrder} disabled={savingOrder || viewList.length === 0}>
                  {savingOrder ? "Saving…" : "Save order"}
                </Button>
                <Button variant="outline-secondary" onClick={resetOrder}>Reset view order</Button>

                <hr />
                <Button
                  variant="outline-success"
                  onClick={shuffleOptionsForVisible}
                  title="MCQ / Multi-correct / MCQ+Desc only"
                >
                  Shuffle options (MCQ types)
                </Button>
                <Button
                  variant="success"
                  onClick={saveOptionShuffle}
                  disabled={savingOptions}
                  title="Writes shuffled options to the questions"
                >
                  {savingOptions ? "Saving…" : "Save shuffled options"}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Right: Questions list with inline CRUD */}
        <Col lg={8}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent d-flex justify-content-between align-items-center">
              <div className="fw-semibold">Questions</div>
              <Badge bg="light" text="dark" className="border">{viewList.length}</Badge>
            </Card.Header>
            <Card.Body className="p-2">
              {viewList.length === 0 ? (
                <div className="p-3 text-muted">No questions yet.</div>
              ) : (
                <div className="qb-grid">
                  {viewList.map((rawQ) => {
                    const q = effective(rawQ);
                    return (
                      <div key={q.id} className="qb-card">
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div>
                            <Badge bg="dark" className="me-2">Q{q._num}</Badge>
                            <Badge bg="light" text="dark" className="border me-1">{q._sLabel || q.subject || "-"}</Badge>
                            <Badge bg="light" text="dark" className="border me-1">{q._secLabel || q.sectionKey || ""}</Badge>
                            <Badge bg="info" className="me-1">{getType(q) || "-"}</Badge>
                            <Badge
                              bg={q.difficulty === "Hard" ? "danger" : q.difficulty === "Easy" ? "success" : "secondary"}
                            >{q.difficulty || "Medium"}</Badge>
                          </div>
                          <Dropdown align="end">
                            <Dropdown.Toggle size="sm" variant="outline-secondary">
                              <i className="bi bi-three-dots-vertical" />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Header>Mapping</Dropdown.Header>
                              <Dropdown.Item as="div" style={{ minWidth: 260 }}>
                                <div className="p-2">
                                  <Form.Label className="small mb-1">Subject</Form.Label>
                                  <Form.Control
                                    size="sm"
                                    defaultValue={q.subject || q._sLabel || ""}
                                    onBlur={(e) => {
                                      const v = e.target.value;
                                      if (v !== (q.subject || "")) updateMeta(q, { subject: v });
                                    }}
                                    disabled={!!busy[q.id]}
                                    placeholder="Subject"
                                  />
                                  <Form.Label className="small mb-1 mt-2">Section</Form.Label>
                                  <Form.Control
                                    size="sm"
                                    defaultValue={q.sectionKey || q._secKey || ""}
                                    onBlur={(e) => {
                                      const v = e.target.value;
                                      if (v !== (q.sectionKey || "")) updateMeta(q, { sectionKey: v });
                                    }}
                                    disabled={!!busy[q.id]}
                                    placeholder="Section"
                                  />
                                </div>
                              </Dropdown.Item>

                              <Dropdown.Header>Marks</Dropdown.Header>
                              <Dropdown.Item as="div" style={{ minWidth: 260 }}>
                                <div className="p-2">
                                  <Form.Label className="small mb-1">Marks</Form.Label>
                                  <Form.Control
                                    size="sm"
                                    inputMode="decimal"
                                    defaultValue={typeof q.marks === "number" ? q.marks : 1}
                                    onBlur={(e) => {
                                      const v = Number(e.target.value || 0);
                                      if (!Number.isFinite(v)) return;
                                      if (v !== q.marks) updateMeta(q, { marks: v });
                                    }}
                                    disabled={!!busy[q.id]}
                                  />
                                  <Form.Label className="small mb-1 mt-2">Negative Marks</Form.Label>
                                  <Form.Control
                                    size="sm"
                                    inputMode="decimal"
                                    defaultValue={typeof q.negativeMarks === "number" ? q.negativeMarks : 0}
                                    onBlur={(e) => {
                                      const v = Number(e.target.value || 0);
                                      if (!Number.isFinite(v)) return;
                                      if (v !== q.negativeMarks) updateMeta(q, { negativeMarks: v });
                                    }}
                                    disabled={!!busy[q.id]}
                                  />
                                </div>
                              </Dropdown.Item>

                              <Dropdown.Divider />
                              <Dropdown.Item className="text-danger" onClick={() => removeQuestion(q)}>
                                <i className="bi bi-trash me-2" /> Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>

                        {/* Stem */}
                        {renderStemBlock(q)}

                        {/* Options / Type-specific content */}
                        {renderOptionsBlock(q)}

                        {/* MCQ+Desc descriptive add-on */}
                        {getType(q) === "mcq_descriptive" && q.descPartHtml && (
                          <>
                            <div className="text-muted small mt-2">Descriptive Part</div>
                            <div className="qb-html" dangerouslySetInnerHTML={{ __html: q.descPartHtml }} />
                          </>
                        )}

                        {/* Explanation (folded) */}
                        {q.explanationHtml && (
                          <details className="mt-2">
                            <summary className="small text-muted">Explanation</summary>
                            <div className="text-muted mt-1 qb-html" dangerouslySetInnerHTML={{ __html: q.explanationHtml }} />
                          </details>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Preview modal: full paper then answers & explanations at end */}
      <Modal show={previewOpen} onHide={() => setPreviewOpen(false)} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Preview Paper · {tpl.name || ""}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* ===== Questions ===== */}
          <h6 className="mb-3">Questions</h6>
          {viewList.map(raw => {
            const q = effective(raw);
            return (
              <div key={`pv-${q.id}`} className="mb-4">
                <div className="fw-semibold">Q{q._num}.</div>
                {renderStemBlock(q)}
                {renderOptionsBlock(q)}
                {getType(q) === "mcq_descriptive" && q.descPartHtml && (
                  <>
                    <div className="text-muted small mt-2">Descriptive Part</div>
                    <div className="qb-html" dangerouslySetInnerHTML={{ __html: q.descPartHtml }} />
                  </>
                )}
              </div>
            );
          })}

          <hr />

          {/* ===== Answers & Explanations ===== */}
          <h6 className="mb-3">Answers &amp; Explanations</h6>
          {viewList.map(raw => {
            const q = effective(raw);
            return (
              <div key={`ans-${q.id}`} className="mb-3">
                <div className="fw-semibold">
                  Q{q._num}: <span className="text-success">{computeAnswerString(q)}</span>
                </div>
                {(q.explanationHtml || (getType(q) === "mcq_descriptive" && q.descPartHtml)) && (
                  <div className="text-muted mt-1 qb-html" dangerouslySetInnerHTML={{ __html: q.explanationHtml || q.descPartHtml }} />
                )}
              </div>
            );
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPreviewOpen(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Local styles */}
      <style>{`
        .tdb-eyebrow { font-size: 12px; letter-spacing:.08em; text-transform: uppercase; color: var(--bs-secondary-color); }
        .qb-grid {
          display:grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          gap:12px;
        }
        @media (max-width:576px){ .qb-grid{ grid-template-columns:1fr; } }
        .qb-card {
          border:1px solid var(--border);
          border-radius:14px; padding:12px;
          background: color-mix(in oklab, var(--surface) 96%, transparent);
        }
        .qb-html :where(img,video,iframe){ max-width:100%; height:auto; }
        .qb-html table { width:100%; overflow:auto; display:block; }
      `}</style>
    </div>
  );
}
